import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationService } from "@/features/notifications/services/notificationService";
import { handleApiError, toastMutationSuccess } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type {
  ListNotificationsQuery,
  NotificationPreferences,
} from "@/types/notification";

function invalidateInboxQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
}

export function useNotificationInbox(query?: ListNotificationsQuery) {
  return useQuery({
    queryKey: queryKeys.notifications.inbox(query),
    queryFn: () => notificationService.listInbox(query),
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 60_000,
  });
}

export function useNotificationDetail(id: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.notifications.detail(id ?? ""),
    queryFn: () => notificationService.getDetail(id!),
    enabled: enabled && Boolean(id),
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onSuccess: () => invalidateInboxQueries(queryClient),
    onError: (error) => handleApiError(error),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => invalidateInboxQueries(queryClient),
    onError: (error) => handleApiError(error),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: () => notificationService.getPreferences(),
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  return useMutation<NotificationPreferences, Error, NotificationPreferences>({
    mutationFn: (data) => notificationService.updatePreferences(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.preferences(),
      });
      toastMutationSuccess(res, { fallbackMessage: "تم تحديث تفضيلاتك بنجاح" });
    },
    onError: (error) => handleApiError(error),
  });
}
